/** The prototype's `toast(msg)` (subscription-portal.html `toast()`): one
 * transient message, bottom-centre, that fades out after 2.6s; a second
 * toast replaces the first rather than stacking. MS-819 shared primitives
 * (C-24), consumed by MS-820 ("Purchase confirmed"), MS-821 (Excess) and
 * MS-822 (User Limits).
 *
 * A tiny module-level store rather than a context provider: `toast()` works
 * from any page, hook or test without a provider in the tree, and the one
 * `<Toaster />` (components/ui/Toast.tsx) AppShell renders is the only
 * subscriber that paints it. */

export const TOAST_VISIBLE_MS = 2600

export interface ToastState {
  message: string
  /** Bumped per toast so an identical message still restarts the timer. */
  seq: number
  visible: boolean
}

let state: ToastState = { message: '', seq: 0, visible: false }
const listeners = new Set<() => void>()

function emit(next: ToastState) {
  state = next
  listeners.forEach((l) => l())
}

export function subscribeToast(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getToastState(): ToastState {
  return state
}

/** Show `message` (already translated — call sites pass `t(...)`). */
export function toast(message: string): void {
  emit({ message, seq: state.seq + 1, visible: true })
}

/** Fade the current toast out (the Toaster's timer calls this). */
export function hideToast(): void {
  if (state.visible) emit({ ...state, visible: false })
}

/** Hook form of `toast`, for call sites that prefer a hook. */
export function useToast(): (message: string) => void {
  return toast
}
